
















































import { Component, Mixins, Watch } from 'vue-property-decorator'
import BuilderNavigationStep from '@/components/builder/BuilderNavigationStep.vue'
import BuilderDefaultContainer from '@/components/builder/BuilderDefaultContainer.vue'
import BuilderDeviceProfileStub from '@/components/builder/BuilderDeviceProfileStub.vue'
import BuilderSidebar from '@/components/builder/BuilderSidebar.vue'
import { mapGetters, mapState } from 'vuex'
import { Profile } from '@/store/builder/types'
import { Location } from 'vue-router'
import { App } from '@/services/types'
import WithBuilderStubProperties from '@/mixins/WithBuilderStubProperties'
import { LocaleMessage } from 'vue-i18n'

interface Navigation {
  label: string | LocaleMessage
  to: Location
  checked: boolean
  show: boolean
}

export interface ProfileStubState {
  color: string
  siteName: string | undefined
  headerIcon: string | undefined
  highlight: string | null
  blurProfile: boolean
  avatar: File | string | undefined
  description: string | undefined
  blurCover: boolean
  cover: { id: number; file: File | string | null }[]
  blurSubscriptions: boolean
  showSubscribeButton: boolean
  subscriptions: {
    id: number
    name: string
    price: string
    duration: number
    frequency: number
  }[]
  blurContentType: boolean
  blurPosts: boolean
  currency: string
  posts: { id: number; file: File | string | null }[]
}

@Component({
  components: {
    BuilderSidebar,
    BuilderNavigationStep,
    BuilderDefaultContainer,
    BuilderDeviceProfileStub,
  },
  computed: {
    ...mapGetters('builder', { profile: 'getProfile', app: 'getApp', appType: 'getAppType' }),
  },
})
export default class BuilderProfile extends Mixins(WithBuilderStubProperties) {
  app!: App | null

  profile!: Profile

  stubToMerge: Partial<ProfileStubState> = {}

  appType!: string

  get partialStub(): Partial<ProfileStubState> {
    return this.stub
  }

  set partialStub(value: Partial<ProfileStubState>) {
    this.stubToMerge = { ...this.stubToMerge, ...value }
  }

  get stub(): ProfileStubState {
    return {
      ...{
        color: this.stubColor,
        siteName: this.stubSiteName,
        highlight: null,
        headerIcon: '',
        blurProfile: false,
        avatar: this.stubAvatar,
        description: this.stubDescription,
        blurCover: false,
        cover: this.stubCover,
        blurSubscriptions: false,
        showSubscribeButton: false,
        subscriptions: this.stubSubscriptions,
        blurContentType: false,
        blurPosts: false,
        posts: this.stubPosts,
        currency: 'RUB',
      },
      ...this.stubToMerge,
    }
  }

  get currentIndex(): number {
    for (let i = 0; i < this.navigation.length; i += 1) {
      if (this.navigation[i].to.name === this.$route.name) {
        return i
      }
    }

    return 0
  }

  get navigation(): Navigation[] {
    const { highlight } = this.stub
    const navigation = [
      {
        label: this.$t('colorScheme'),
        to: { name: 'create' },
        checked: !!this.profile.color,
        show: true,
      },
      {
        label: this.$t('pageName'),
        to: { name: 'profile.page_name' },
        checked: !!this.profile.name,
        show: true,
      },

      ...(this.app
        ? [
            {
              label: this.$t('domainName'),
              to: { name: 'profile.domain_name' },
              checked: !!this.profile.name,
              show: !!this.appType,
            },
            {
              label: this.$t('yourAvatar'),
              to: { name: 'profile.avatar' },
              checked: !!this.profile.avatar,
              show: !!this.appType,
            },
            {
              label: this.$t('pageStatistics'),
              to: { name: 'profile.statistic' },
              checked: !!this.profile.avatar,
              show: !!this.profile.avatar,
            },
            {
              label: this.$t('pageDescription'),
              to: { name: 'profile.page_description' },
              checked: !!this.profile.description,
              show: !!this.profile.avatar,
            },
            {
              label: this.$t('pageCover'),
              to: { name: 'profile.page_cover' },
              checked: !!this.profile.cover?.length,
              show: !!this.profile.description,
            },
            {
              label: this.$t('yourSubs'),
              to: { name: 'profile.followers' },
              checked: !!this.profile.subscriptions?.length,
              show: !!(this.profile.cover?.length && this.appType === 'TYPE_BLOGER'),
            },
            {
              label: this.$t('contentTypes'),
              to: { name: 'profile.content' },
              checked: !!this.profile.subscriptions?.length || (this.appType === 'TYPE_STORE' && !!this.profile.cover?.length),
              show: !!this.profile.subscriptions?.length || (this.appType === 'TYPE_STORE' && !!this.profile.cover?.length),
            },
            {
              label: this.$t('productAdd.title'),
              to: { name: 'profile.product_add' },
              checked: !!this.profile.cover?.length,
              show: this.appType === 'TYPE_STORE' && !!this.profile.cover?.length,
            },
            {
              label: this.$t('postCreation'),
              to: { name: 'profile.create_post' },
              checked: !!this.profile.posts?.length,
              show: !!this.profile.subscriptions?.length || (this.appType === 'TYPE_STORE' && !!this.profile.cover?.length),
            },
          ]
        : []),
    ]

    return this.app
      ? navigation
          .filter((n) => n.show)
          .map((n) => ({
            ...n,
            to: { ...n.to, params: { id: String(this.app?.id) } },
          }))
      : navigation
  }

  @Watch('$route', { immediate: true, deep: true })
  onRouteChange(): void {
    this.stubToMerge = {}
  }

  async onBack(): Promise<void> {
    if (this.currentIndex > 0) {
      const nav = this.navigation[this.currentIndex - 1]
      // this.$store.commit("builder/setCurrentStepName", nav.to.name)
      await this.$router.push(nav.to)
    }
  }

  async onNext(): Promise<void> {
    if (this.currentIndex < this.navigation.length - 1) {
      const nav = this.navigation[this.currentIndex + 1]
      // this.$store.commit("builder/setCurrentStepName", nav.to.name)
      await this.$router.push(nav.to)

      return
    }

    await this.$router.push({
      name: 'profile-created',
      params: this.app ? { id: String(this.app.id) } : {},
    })
  }
}
